import { innerJoin, intersection, isNil, pathOr } from 'ramda';
import type {
  NavigationOption,
  UserPermission,
} from '../../containers/App/types';
import { featureFlagNameMap, inverseFeatureFlagNameMap } from './constants';

export const filterNavigationOptionsByPermissionsPropertyClass = ({
  navigationOptions,
  selectedProperty,
  userPermissions,
  selectedPropertySubjournals,
  paymentProvider,
  flags,
  hasActiveTwoWayProperties = false,
}): Array<NavigationOption> => {
  const propertyClass = pathOr('', ['propertyClass', 'name'], selectedProperty);

  const permissionsFilter = innerJoin(
    (userPermission: UserPermission, navOptionScope: string) =>
      userPermission.scope === navOptionScope &&
      (!userPermission.propertyLevel || !isNil(selectedProperty)),
    userPermissions,
  );

  const propertyClassFilter = (option: Object): boolean => {
    const programFilter = option.affordableProgramFilter;
    const hasAffordableFilter =
      programFilter && Array.isArray(programFilter) && programFilter.length > 0;
    return !(hasAffordableFilter && propertyClass === 'Conventional');
  };

  const propertyAffordablePrograms = (selectedProperty?.pap ?? []).map(
    (pap) => pap?.masterAffordableProgram?.name,
  );

  const affordableProgramFilter = (option: Object): boolean => {
    const programFilter = option.affordableProgramFilter;
    if (
      !programFilter ||
      !Array.isArray(programFilter) ||
      programFilter.length === 0
    ) {
      return true;
    }
    if (!selectedProperty) {
      return false;
    }
    // Handle wildcard '*' - show for any property with affordable programs
    if (programFilter.includes('*')) {
      return propertyAffordablePrograms.length > 0;
    }
    return programFilter.some((program) =>
      propertyAffordablePrograms.includes(program),
    );
  };
  const hasCommercialFloorPlans = pathOr(
    'NONE',
    ['hasCommercialFloorPlans'],
    selectedProperty,
  );
  const commercialFloorPlanFilter = (option: Object): boolean => {
    if (
      option.name === 'Manage Commercial Rent Roll' ||
      option.name === 'Add Commercial Applicant'
    ) {
      return (
        hasCommercialFloorPlans === 'ALL' || hasCommercialFloorPlans === 'SOME'
      );
    }
    if (option.name === 'Manage Rent Roll') {
      return (
        hasCommercialFloorPlans === 'NONE' || hasCommercialFloorPlans === 'SOME'
      );
    }
    if (
      option.name === 'Manage Prospects' ||
      option.name === 'Add Prospect' ||
      option.name === 'Manage Prior Residents'
    ) {
      return hasCommercialFloorPlans === 'ALL' ? false : true;
    }
    if (option.name === 'Manage Prior Tenants') {
      return hasCommercialFloorPlans !== 'ALL' ? false : true;
    }

    return true;
  };
  const subsidySubjournalFilter = (option) => {
    if (option.name === 'Manage Subsidy Payments') {
      const hasSubsidySubjournal = selectedPropertySubjournals.filter(
        (subjournal) => {
          const subjournalType =
            subjournal?.masterSubjournalType?.description ?? null;
          return subjournalType === 'Subsidy';
        },
      );
      return hasSubsidySubjournal.length ? true : false;
    }
    return true;
  };
  const paymentProviderFilter = (option) => {
    if (option.name === 'Manage Online Payments') {
      return paymentProvider?.paymentProvider === 'FORTRESS';
    }
    return true;
  };
  const twoWayCommunicationFilter = (option) => {
    if (option.name === 'Message Hub' && !option.portfolioSummaryOnly) {
      return selectedProperty?.isTwoWayCommunicationActive === true;
    }
    return true;
  };

  const connectPortfolioFilter = (option) => {
    if (
      option.name === 'Communications' &&
      option.portfolioSummaryOnly === true &&
      !selectedProperty
    ) {
      return hasActiveTwoWayProperties === true;
    }
    return true;
  };
  const selectedPropertyFilter = (option) => {
    if (option.name === 'Prorate Calculator') {
      // Prorate calculator needs a selected property since it uses prop tx codes
      return !isNil(selectedProperty);
    }
    return true;
  };

  const parentNameById = navigationOptions.reduce((acc, opt) => {
    if (opt.id) acc[opt.id] = opt.name;
    return acc;
  }, {});

  const featureFlagFilter = (option) => {
    let optionName = option.name;
    if (option.portfolioSummaryOnly) {
      optionName = optionName + '-portfolioSummaryOnly';
    }
    const featureFlagName = featureFlagNameMap[optionName];
    const showByFlag = flags[featureFlagName] ?? true;

    const parentName = option.parent ? parentNameById[option.parent] : null;
    const inverseKey = parentName
      ? `${option.name}@${parentName}`
      : option.name;
    const inverseFlagName = inverseFeatureFlagNameMap[inverseKey];
    const hideByInverseFlag =
      inverseFlagName !== undefined && flags[inverseFlagName] === true;

    return showByFlag && !hideByInverseFlag;
  };

  const checkPermissionsOnlyFilter = innerJoin(
    (userPermission: UserPermission, navOptionScope: string) =>
      userPermission.scope === navOptionScope,
    userPermissions,
  );

  const filteredOptions = navigationOptions.map((option) => {
    const children = option.children.filter((subOption) => {
      const filteredByPermissions =
        (subOption.portfolioSummaryOnly
          ? checkPermissionsOnlyFilter(subOption.scope)
          : permissionsFilter(subOption.scope)
        )?.length > 0 || subOption.scope?.length === 0;

      const filteredbyPropertyClass = propertyClassFilter(subOption);
      const filteredbyCommercialFloorPlan =
        commercialFloorPlanFilter(subOption);
      const filteredByPropertySubjournals = subsidySubjournalFilter(subOption);
      const filteredByPaymentProvider = paymentProviderFilter(subOption);
      const filteredByTwoWayCommunication =
        twoWayCommunicationFilter(subOption);
      const filteredByFeatureFlags = featureFlagFilter(subOption);
      const filteredByAffordableProgram = affordableProgramFilter(subOption);
      return (
        filteredByPermissions &&
        filteredbyPropertyClass &&
        filteredbyCommercialFloorPlan &&
        filteredByPropertySubjournals &&
        filteredByPaymentProvider &&
        filteredByTwoWayCommunication &&
        filteredByFeatureFlags &&
        filteredByAffordableProgram
      );
    });
    return {
      ...option,
      children,
    };
  });

  const filteredForPortfolioSummary = (option) => {
    if (option.portfolioSummaryOnly) {
      if (!selectedProperty?.id) return true;
      if (selectedProperty?.id) return false;
    }
    return true;
  };

  const checkUserPermissions = (scope: Array<string>) => {
    const userScopes = userPermissions.map((permission) => permission.scope);
    return intersection(scope, userScopes).length > 0;
  };

  const options = filteredOptions.filter((option) => {
    const filteredBySelectedProperty = selectedPropertyFilter(option);
    const filteredByFeatureFlags = featureFlagFilter(option);
    const filteredByPropertyClass = propertyClassFilter(option);
    const filteredByAffordableProgram = affordableProgramFilter(option);
    const filteredByConnectPortfolio = connectPortfolioFilter(option);
    let filteredByPermissions =
      permissionsFilter(option.scope).length > 0 || option.scope.length === 0;

    const showInPortfolioSummaryOnly = filteredForPortfolioSummary(option);

    if (option.portfolioSummaryOnly) {
      filteredByPermissions =
        checkPermissionsOnlyFilter(option.scope).length > 0 ||
        option.scope.length === 0;
    }

    return (
      filteredByFeatureFlags &&
      filteredByPermissions &&
      filteredBySelectedProperty &&
      filteredByPropertyClass &&
      filteredByAffordableProgram &&
      showInPortfolioSummaryOnly &&
      filteredByConnectPortfolio
    );
  });

  const optionsWithNewPropertyURL = options.map((op) => {
    const selectedPropertyId = selectedProperty?.id;
    const selectedPropertyUrl = selectedPropertyId
      ? `/property/${selectedPropertyId}`
      : '';

    if (op.children) {
      op.children = op.children.map((childOp) => {
        const childUrlWithPropertyId = `${selectedPropertyUrl}${childOp.url}`;
        const url = childOp.hasPropertyURL
          ? childUrlWithPropertyId
          : childOp.url;
        return { ...childOp, url };
      });
    }
    const urlWithPropertyId = op.url ? `${selectedPropertyUrl}${op.url}` : null;
    const url = op.hasPropertyURL ? urlWithPropertyId : op.url;
    return {
      ...op,
      url,
    };
  });

  const newNavigationOptions = [...optionsWithNewPropertyURL];
  const { centralizedWorkOrders } = flags;

  if (
    centralizedWorkOrders &&
    !selectedProperty &&
    checkUserPermissions(['workorder-read'])
  ) {
    newNavigationOptions.push({
      children: [
        {
          icon: null,
          id: '1',
          name: 'Manage Work Orders',
          openTab: false,
          order: 2,
          scope: ['workorder-read'],
          translations: {
            en: 'Manage Work Orders',
            es: 'Manage Work Orders',
          },
          url: '/work-orders',
          parent: '1',
        },
      ],
      icon: 'et-services',
      name: 'Maintenance',
      openTab: false,
      order: 3,
      scope: [],
      translations: { en: 'Maintenance', es: 'Maintenance' },
    });
  }

  return newNavigationOptions;
};

export const determineNavigationToOnClick = ({
  url,
  openTab,
  setOpen,
  isXs,
}) => {
  if (openTab && url) {
    return {
      onClick: () => {
        if (isXs) {
          setOpen(false);
        }
      },
      target: '_blank',
      href: url,
    };
  } else if (url) {
    return {
      onClick: () => {
        if (isXs) {
          setOpen(false);
        }
      },
      to: url,
    };
  }
  return null;
};

// Indicator precedence: Leslie Needs Help > New Message > Unresolved Message, applied both to a
// single route and to the parent group that fans out over several.
export const getOptionNotificationsBadgeType = (
  twoWayNotifications,
  optionName,
) => {
  const getBadgeType = (category) => {
    const {
      hasConversationsNeedingHelp,
      hasNewMessages,
      hasUnresolvedConversations,
    } = twoWayNotifications[category];
    if (hasConversationsNeedingHelp) return 'needs-help';
    if (hasNewMessages) return 'new';
    if (hasUnresolvedConversations) return 'unresolved';
    return null;
  };
  const getBadgeTypeForGroup = (categories) => {
    const badgeTypes = categories.map((category) => getBadgeType(category));
    if (badgeTypes.includes('needs-help')) return 'needs-help';
    if (badgeTypes.includes('new')) return 'new';
    if (badgeTypes.includes('unresolved')) return 'unresolved';
    return null;
  };

  if (optionName === 'Manage Prospects') return getBadgeType('prospects');
  if (optionName === 'Manage Applications') return getBadgeType('applications');
  if (optionName === 'Manage Rent Roll') return getBadgeType('rentRoll');
  if (optionName === 'Manage Prior Residents')
    return getBadgeType('priorResidents');
  if (optionName === 'Sales')
    return getBadgeTypeForGroup(['prospects', 'applications']);
  if (optionName === 'People')
    return getBadgeTypeForGroup(['rentRoll', 'priorResidents']);
  if (optionName === 'Communications') return getBadgeType('messagingHub');
  if (optionName === 'Messaging Hub') return getBadgeType('messagingHub');

  return null;
};
